import React, { useState } from 'react';
import { useGetList, useGetMany } from 'react-admin';
import {
	Box,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	Link as MuiLink,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { EditCommissionDialog } from './EditCommissionDialog';
import { Commission, displayStatus, eur, fmtDate, isEditable } from './commissionFormat';

// Lignes de commission d'un partenaire, vues depuis l'admin Quanti.
//
// Le wallet juste au-dessus donne des SOLDES. Il ne dit pas d'où ils viennent : une
// somme fausse s'y lit comme une somme, sans moyen de savoir quelle ligne a dérapé.
// Ce tableau existe pour vérifier le commissionnement AU CAS PAR CAS — quel paiement
// client, quel taux appliqué, quelle facture Stripe à l'origine.
//
// L'origine (facture Stripe, ou règlement saisi à la main) et tout le reste de la ligne
// sont dans la modale de détail, qui permet aussi de retoucher le taux d'une ligne non
// réglée (cf. EditCommissionDialog).

// #region PartnerCommissions
export const PartnerCommissions: React.FC<{ partnerId: string }> = ({ partnerId }) => {
	// Filtre en SNAKE_CASE (`partner_id`) : côté API la clé est concaténée à un nom de
	// colonne SQL. Un `partnerId` en camelCase serait ignoré et la liste montrerait les
	// commissions de TOUS les partenaires.
	const [selected, setSelected] = useState<Commission | null>(null);
	const { data, isLoading, refetch } = useGetList<any>(
		'commissions',
		{ pagination: { page: 1, perPage: 200 }, sort: { field: 'collected_at', order: 'DESC' }, filter: { partner_id: partnerId } },
		{ enabled: !!partnerId }
	);

	const rows = (data || []) as Commission[];

	const baIds = Array.from(new Set(rows.map((r) => r.billingAccountId).filter(Boolean)));
	const { data: accounts } = useGetMany<any>('billing-accounts', { ids: baIds }, { enabled: baIds.length > 0 });
	const nameById = new Map<string, string>((accounts || []).map((a: any) => [String(a.id), a.name as string]));

	const total = rows.reduce((s, r) => s + (r.amount || 0), 0);

	return (
		<Card sx={{ mb: 2 }}>
			<CardContent>
				<Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
					<Typography variant="h6">Lignes de commission</Typography>
					{rows.length > 0 && (
						<Typography variant="body2" color="text.secondary">
							{rows.length} ligne{rows.length > 1 ? 's' : ''} · total {eur(total)}
						</Typography>
					)}
				</Box>

				{isLoading ? (
					<Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
						<CircularProgress size={24} />
					</Box>
				) : rows.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Aucune commission pour ce partenaire.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Encaissé le</TableCell>
								<TableCell>Client</TableCell>
								{/* « HT » explicite : la commission s'assied sur le hors-taxes, la TVA
								    étant collectée pour l'État. Sans la mention, un écart de 20 % avec le
								    montant Stripe se lit comme un bug. */}
								<TableCell align="right">Encaissé HT</TableCell>
								<TableCell align="right">Taux</TableCell>
								<TableCell align="right">Commission</TableCell>
								<TableCell>Statut</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((c) => {
								const st = displayStatus(c);
								return (
									<TableRow key={c.id}>
										<TableCell>{fmtDate(c.collectedAt)}</TableCell>
										<TableCell>
											<MuiLink
												component={RouterLink}
												to={`/billing-accounts/${c.billingAccountId}`}
												variant="body2"
												underline="hover"
											>
												{nameById.get(c.billingAccountId) || c.billingAccountId}
											</MuiLink>
											{c.attributionId && (
												<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
													Attribution #{c.attributionId}
												</Typography>
											)}
										</TableCell>
										<TableCell align="right">{eur(c.collectedAmount)}</TableCell>
										{/* Le taux est affiché À CÔTÉ de l'encaissé et de la commission :
										    c'est le triplet qui permet de refaire le calcul de tête et de
										    repérer un taux qui n'est pas celui de la grille. */}
										<TableCell align="right">
											{c.rate ? `${c.rate} %` : '—'}
											{c.forced && (
												<Tooltip title={c.editNote || 'Retouchée à la main'}>
													<Chip label="Forcé" size="small" variant="outlined" sx={{ ml: 1 }} />
												</Tooltip>
											)}
										</TableCell>
										<TableCell align="right" sx={{ fontWeight: 700 }}>
											{eur(c.amount)}
										</TableCell>
										<TableCell>
											<Chip label={st.label} size="small" color={st.color} />
										</TableCell>
										<TableCell align="right">
											<Tooltip title={isEditable(c) ? 'Détail et retouche' : 'Détail'}>
												<IconButton size="small" onClick={() => setSelected(c)}>
													{isEditable(c) ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				)}
				<EditCommissionDialog
					commission={selected}
					clientName={selected ? nameById.get(selected.billingAccountId) : undefined}
					onClose={() => setSelected(null)}
					onSaved={() => refetch()}
				/>
			</CardContent>
		</Card>
	);
};
// #endregion
